import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Progress,
  Descriptions,
  Tooltip,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../services/api';

interface KeyInfo {
  key: string;
  type: string;
  ttl: number;
}

interface MemoryStats {
  usedMemory: number;
  usedMemoryHuman: string;
  maxMemory: number;
  maxMemoryHuman: string;
  usedMemoryPeak: number;
  usedMemoryPeakHuman: string;
  usagePercent: number;
  memFragmentationRatio: string;
}

interface RedisStats {
  dbSize: number;
  connectedClients: string;
  totalConnectionsReceived: string;
  totalCommandsProcessed: string;
  uptimeInSeconds: string;
  uptimeInDays: string;
  redisVersion: string;
}

const RedisMonitor: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [keys, setKeys] = useState<KeyInfo[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [searchPattern, setSearchPattern] = useState('*');
  const [cursor, setCursor] = useState<number>(0);
  const [hasMore, setHasMore] = useState(false);
  const [memoryStats, setMemoryStats] = useState<MemoryStats | null>(null);
  const [redisStats, setRedisStats] = useState<RedisStats | null>(null);
  const [valueModalVisible, setValueModalVisible] = useState(false);
  const [currentKeyValue, setCurrentKeyValue] = useState<any>(null);

  // 获取内存统计
  const fetchMemoryStats = useCallback(async () => {
    try {
      const res = await api.get('/redis/memory');
      setMemoryStats(res.data);
    } catch (error) {
      console.error('获取内存统计失败:', error);
    }
  }, []);

  // 获取Redis统计
  const fetchRedisStats = useCallback(async () => {
    try {
      const res = await api.get('/redis/stats');
      setRedisStats(res.data);
    } catch (error) {
      console.error('获取Redis统计失败:', error);
    }
  }, []);

  // 获取Key列表
  const fetchKeys = useCallback(async (newCursor = 0, append = false) => {
    setLoading(true);
    try {
      const res = await api.get('/redis/keys', {
        params: { pattern: searchPattern, cursor: newCursor, count: 50 },
      });
      if (append) {
        setKeys(prev => [...prev, ...(res.data.keys || [])]);
      } else {
        setKeys(res.data.keys || []);
      }
      setCursor(res.data.nextCursor);
      setHasMore(res.data.hasMore);
    } catch (error) {
      message.error('获取Key列表失败');
    } finally {
      setLoading(false);
    }
  }, [searchPattern]);

  useEffect(() => {
    fetchMemoryStats();
    fetchRedisStats();
    fetchKeys();
  }, []);

  // 搜索
  const handleSearch = () => {
    setCursor(0);
    fetchKeys(0, false);
  };

  // 加载更多
  const handleLoadMore = () => {
    fetchKeys(cursor, true);
  };

  // 刷新
  const handleRefresh = () => {
    fetchMemoryStats();
    fetchRedisStats();
    setCursor(0);
    fetchKeys(0, false);
  };

  // 查看Key值
  const handleViewValue = async (key: string) => {
    try {
      const res = await api.get('/redis/key/value', { params: { key } });
      setCurrentKeyValue(res.data);
      setValueModalVisible(true);
    } catch (error) {
      message.error('获取Key值失败');
    }
  };

  // 删除Key
  const handleDeleteKeys = async (keysToDelete: string[]) => {
    try {
      await api.delete('/redis/keys', { data: { keys: keysToDelete } });
      message.success(`成功删除 ${keysToDelete.length} 个Key`);
      setSelectedKeys([]);
      fetchKeys(0, false);
      fetchRedisStats();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 格式化TTL
  const formatTTL = (ttl: number) => {
    if (ttl === -1) return <Tag color="blue">永不过期</Tag>;
    if (ttl === -2) return <Tag color="red">已过期</Tag>;
    if (ttl < 60) return <Tag color="orange">{ttl}秒</Tag>;
    if (ttl < 3600) return <Tag color="orange">{Math.floor(ttl / 60)}分钟</Tag>;
    if (ttl < 86400) return <Tag color="green">{Math.floor(ttl / 3600)}小时</Tag>;
    return <Tag color="green">{Math.floor(ttl / 86400)}天</Tag>;
  };

  // 格式化运行时间
  const formatUptime = (seconds: string) => {
    const s = parseInt(seconds || '0');
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);
    return `${days}天 ${hours}小时 ${mins}分钟`;
  };

  const columns: ColumnsType<KeyInfo> = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      ellipsis: true,
      render: (key) => (
        <Tooltip title={key}>
          <span style={{ fontFamily: 'monospace' }}>{key}</span>
        </Tooltip>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => {
        const colorMap: Record<string, string> = {
          string: 'green',
          list: 'blue',
          set: 'purple',
          zset: 'orange',
          hash: 'cyan',
        };
        return <Tag color={colorMap[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'TTL',
      dataIndex: 'ttl',
      key: 'ttl',
      width: 120,
      render: (ttl) => formatTTL(ttl),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewValue(record.key)}
          >
            查看
          </Button>
          <Popconfirm
            title="确定删除此Key?"
            onConfirm={() => handleDeleteKeys([record.key])}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Key总数"
              value={redisStats?.dbSize || 0}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="连接客户端"
              value={redisStats?.connectedClients || 0}
              prefix={<CloudServerOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="命令处理数"
              value={parseInt(redisStats?.totalCommandsProcessed || '0')}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="运行时间"
              value={formatUptime(redisStats?.uptimeInSeconds || '0')}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ fontSize: 16 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 内存使用 */}
      <Card title="内存使用" style={{ marginBottom: 16 }}>
        <Row gutter={24}>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <Progress
                type="dashboard"
                percent={Math.round(memoryStats?.usagePercent || 0)}
                status={
                  (memoryStats?.usagePercent || 0) > 80
                    ? 'exception'
                    : (memoryStats?.usagePercent || 0) > 60
                    ? 'normal'
                    : 'success'
                }
              />
              <div style={{ marginTop: 8 }}>内存使用率</div>
            </div>
          </Col>
          <Col span={16}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="已用内存">
                {memoryStats?.usedMemoryHuman || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="最大内存">
                {memoryStats?.maxMemoryHuman || '未限制'}
              </Descriptions.Item>
              <Descriptions.Item label="峰值内存">
                {memoryStats?.usedMemoryPeakHuman || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="内存碎片率">
                {memoryStats?.memFragmentationRatio || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Redis版本">
                {redisStats?.redisVersion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="总连接数">
                {redisStats?.totalConnectionsReceived || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* Key列表 */}
      <Card
        title="Key管理"
        extra={
          <Space>
            {selectedKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedKeys.length} 个Key?`}
                onConfirm={() => handleDeleteKeys(selectedKeys)}
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除
                </Button>
              </Popconfirm>
            )}
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              刷新
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索模式 (如: user:*)"
            value={searchPattern}
            onChange={(e) => setSearchPattern(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 300 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
        </Space>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={keys}
            rowKey="key"
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys as string[]),
            }}
            pagination={false}
            size="small"
          />
        </Spin>

        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button onClick={handleLoadMore} loading={loading}>
              加载更多
            </Button>
          </div>
        )}
      </Card>

      {/* 查看值弹窗 */}
      <Modal
        title={`Key: ${currentKeyValue?.key}`}
        open={valueModalVisible}
        onCancel={() => setValueModalVisible(false)}
        footer={<Button onClick={() => setValueModalVisible(false)}>关闭</Button>}
        width={700}
      >
        {currentKeyValue && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="类型">
              <Tag>{currentKeyValue.type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="TTL">
              {formatTTL(currentKeyValue.ttl)}
            </Descriptions.Item>
            <Descriptions.Item label="值">
              <pre
                style={{
                  maxHeight: 400,
                  overflow: 'auto',
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {typeof currentKeyValue.value === 'object'
                  ? JSON.stringify(currentKeyValue.value, null, 2)
                  : currentKeyValue.value}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default RedisMonitor;
